import type { SearchPosting } from "@/lib/api/catalogue";
import type { Application, ListApplicationsParams } from "@/lib/api/envoy";
import { jobsLine } from "./card";
import { atCompany, type CompanyRef } from "./company";
import { emptyLine, shownPostings } from "./status";
import { statusGroup } from "./statusGroup";
import { JOBS_PAGE, type JobsStatus } from "./view";

/**
 * Where a tab's list comes from. The catalogue's search, for All jobs and
 * Untracked and for anyone signed out. A member's In queue and Applied tabs
 * page through their own applications in envoy, which the catalogue knows
 * nothing of: no search words, filter or sort narrows them. On one
 * company's page envoy holds them to that company.
 */
export type JobsSource = "catalogue" | "envoy";

/** The tabs a member reads from envoy. */
export type TrackedStatus = "queue" | "applied";

/** Whether the tab is one envoy lists. */
export function isTracked(status: JobsStatus): status is TrackedStatus {
    return status === "queue" || status === "applied";
}

/**
 * Which list the tab reads: envoy for a member's In queue and Applied, on
 * Jobs and on one company's page alike, where envoy holds the list to the
 * company.
 */
export function jobsSource(member: boolean, status: JobsStatus): JobsSource {
    return member && isTracked(status) ? "envoy" : "catalogue";
}

/**
 * Whether In queue and Applied count envoy's totals, of every page. They do
 * where envoy lists those tabs: for a member.
 */
export function countsFromEnvoy(member: boolean): boolean {
    return jobsSource(member, "queue") === "envoy";
}

/** The tablist's name: what its counts are of. */
export function statusTabsLabel(envoyCounts: boolean): string {
    return envoyCounts
        ? "Application status: Untracked counted on this page"
        : "Application status, counted on this page";
}

/**
 * The applications of envoy's page that belong on the tab: those whose
 * place is the tab's own and, on one company's page, those at the company.
 * Envoy already narrows the list; this guards a page from an envoy that
 * does not know `in_queue` or `company` and answers with every
 * application. Envoy's total is left as it was sent.
 */
export function applicationsOnTab(
    applications: readonly Application[],
    status: TrackedStatus,
    company?: CompanyRef,
): Application[] {
    return applications.filter(
        (application) =>
            statusGroup(application) === status &&
            (company === undefined || atCompany(application, company)),
    );
}

/** What envoy is asked to hold a list to on one company's page, and nothing elsewhere. */
function companyParams(company: CompanyRef | undefined): ListApplicationsParams {
    return company ? { source: company.source, company: company.company } : {};
}

/**
 * The envoy list a tracked tab pages through, a page of the board's size
 * from `cursor`, held to the company on one company's page.
 */
export function trackedParams(
    status: TrackedStatus,
    cursor: string | undefined,
    limit: number = JOBS_PAGE,
    company?: CompanyRef,
): ListApplicationsParams {
    const which: ListApplicationsParams =
        status === "queue" ? { inQueue: true } : { status: "APPLIED" };
    return {
        ...which,
        ...companyParams(company),
        limit,
        ...(cursor !== undefined && { cursor }),
    };
}

/**
 * The asks of envoy for In queue's and Applied's totals: a list of one
 * each, held to the company on one company's page.
 */
export function totalsParams(
    company?: CompanyRef,
): [ListApplicationsParams, ListApplicationsParams] {
    return [
        { inQueue: true, ...companyParams(company), limit: 1 },
        { status: "APPLIED", ...companyParams(company), limit: 1 },
    ];
}

/**
 * Whether two asks of envoy are pages of one list, so the last page of
 * one may stand while the next is read. Another tab's page, or another
 * company's, never stands in for this one's.
 */
export function sameTrackedList(
    earlier: ListApplicationsParams | null | undefined,
    later: ListApplicationsParams | null,
): boolean {
    return (
        earlier != null &&
        later !== null &&
        earlier.inQueue === later.inQueue &&
        earlier.status === later.status &&
        earlier.source === later.source &&
        earlier.company === later.company
    );
}

/**
 * The list the pager's cursors belong to. A cursor is only good for the
 * list that gave it, so each envoy tab is a list of its own, each
 * company's apart from Jobs', and every catalogue tab shares the search's.
 */
export function pagerKey(
    source: JobsSource,
    status: JobsStatus,
    search: string,
    company?: CompanyRef,
): string {
    if (source !== "envoy") return `catalogue:${search}`;
    return company ? `envoy:${status}:${company.source}:${company.company}` : `envoy:${status}`;
}

/**
 * The cursor the catalogue's search is asked with. On an envoy tab the
 * pager's cursor is envoy's, so the search stays on its first page, where
 * All jobs opens when the person goes back to it.
 */
export function catalogueCursor(
    source: JobsSource,
    cursor: string | undefined,
): string | undefined {
    return source === "envoy" ? undefined : cursor;
}

/**
 * The page's postings in envoy's order, each read from the catalogue, and
 * each once. One the catalogue no longer has is left off; one still being
 * read waits. Only the record's own entries count, so an id such as
 * `constructor` finds nothing.
 */
export function trackedPostings(
    applications: readonly Application[],
    postings: Readonly<Record<string, SearchPosting | null | undefined>>,
): SearchPosting[] {
    const seen = new Set<string>();
    return applications.flatMap((application) => {
        const postingId = application.posting_id;
        const posting = Object.hasOwn(postings, postingId) ? postings[postingId] : undefined;
        if (!posting || seen.has(postingId)) return [];
        seen.add(postingId);
        return [posting];
    });
}

/** The last page an envoy tab showed, kept to stand in while its next page is read. */
export interface KeptPage {
    /** The list it is a page of: its pager key. */
    list: string;
    /** Its postings' ids, in order, as one string to compare by. */
    ids: string;
    postings: SearchPosting[];
}

/** What an envoy tab shows, and the page to keep for next time. */
export interface TrackedShown {
    postings: SearchPosting[];
    /** The page shown is the last one, kept while this one's postings are read. */
    stale: boolean;
    /** Nothing to show yet. */
    pending: boolean;
    keep: KeptPage | null;
}

/**
 * What an envoy tab shows while its page's postings are read from the
 * catalogue. Once any has come, or every read has ended, the page shows
 * what came: a slow posting does not hold the rest, and a failed one is
 * left off. Until then the tab's last page stands in, with Next off, or,
 * with none to stand in, the board waits. Another list's page never
 * stands in.
 */
export function trackedShown(
    list: string,
    loaded: SearchPosting[],
    reading: boolean,
    kept: KeptPage | null,
): TrackedShown {
    const mine = kept !== null && kept.list === list ? kept : null;
    if (!reading || loaded.length > 0) {
        const ids = loaded.map((posting) => posting.posting_id).join(",");
        const keep = mine !== null && mine.ids === ids ? mine : { list, ids, postings: loaded };
        return { postings: loaded, stale: false, pending: false, keep };
    }
    if (mine !== null) return { postings: mine.postings, stale: true, pending: false, keep: mine };
    return { postings: [], stale: false, pending: true, keep: null };
}

/**
 * The postings whose applications and fits are asked for. On an envoy tab
 * that is the tab's page and the catalogue's, which Untracked counts.
 */
export function askedPostings<Shown extends { posting_id: string }>(
    source: JobsSource,
    board: readonly Shown[],
    catalogue: readonly Shown[],
): Shown[] {
    if (source !== "envoy") return [...catalogue];
    const seen = new Set<string>();
    return [...board, ...catalogue].filter((posting) => {
        if (seen.has(posting.posting_id)) return false;
        seen.add(posting.posting_id);
        return true;
    });
}

/** The state of a list read, as the board needs it. */
export interface ListRead {
    pending: boolean;
    error: Error | null;
    /** The page shown is the last one kept while the next is read. */
    stale: boolean;
}

/** What the board shows of the page it lists: how many, of how many, and where Next leads. */
export interface BoardPage {
    /** How many results this page shows. */
    found: number;
    /** How many the list holds over every page. Undefined until known. */
    total: number | undefined;
    nextCursor: string | null | undefined;
    stale: boolean;
    pending: boolean;
    error: Error | null;
}

/**
 * The board's page. The catalogue's search says its own count and Next;
 * an envoy tab says envoy's, whatever the catalogue holds. A posting
 * envoy lists that the catalogue lost leaves the page one shorter, and
 * envoy's total stands.
 */
export function boardPage(
    source: JobsSource,
    shown: number,
    catalogue: { read: ListRead; total: number | undefined; nextCursor: string | null | undefined },
    envoy: { read: ListRead; total: number | undefined; nextCursor: string | null | undefined },
): BoardPage {
    const from = source === "envoy" ? envoy : catalogue;
    return {
        found: shown,
        total: from.total,
        nextCursor: from.nextCursor,
        stale: from.read.stale,
        pending: from.read.pending,
        error: from.read.error,
    };
}

/**
 * The status tabs' counts. Where envoy lists In queue and Applied, theirs
 * are envoy's totals, of every page, the company's on one company's page;
 * Untracked is of the catalogue's page, the only list of jobs the person
 * has not touched. Where it does not (signed out), every tab counts the
 * page, as `totals` null says. All jobs shows none.
 */
export function tabCounts(
    page: Readonly<Record<JobsStatus, number>> | undefined,
    totals: { queue: number | undefined; applied: number | undefined } | null,
): Partial<Record<JobsStatus, number>> | undefined {
    if (totals === null) return page;
    const counts: Partial<Record<JobsStatus, number>> = {
        ...(page && { open: page.open }),
        ...(totals.queue !== undefined && { queue: totals.queue }),
        ...(totals.applied !== undefined && { applied: totals.applied }),
    };
    return Object.keys(counts).length === 0 ? undefined : counts;
}

/**
 * Whether the pager shows under the results: once the page is read, while
 * it has results, or another page leads on from it or back to it.
 */
export function showsPager(board: BoardPage, hasPrevious: boolean): boolean {
    if (board.pending || board.error) return false;
    return board.found > 0 || hasPrevious || Boolean(board.nextCursor);
}

/** The words beside an envoy tab's count, which say so on one company's page. */
export function trackedLine(
    total: number | undefined,
    status: TrackedStatus,
    company?: CompanyRef,
): string {
    if (total === undefined)
        return status === "queue" ? "Reading your queue." : "Reading what you sent.";
    const noun = total === 1 ? "job" : "jobs";
    const here = company ? " at this company" : "";
    return status === "queue"
        ? `${noun}${here} in your queue, the latest change first.`
        : `${noun}${here} you applied to, the latest change first.`;
}

/** The line in place of an envoy tab's results when none shows. */
export function trackedEmptyLine(
    status: TrackedStatus,
    total: number | undefined,
    company?: CompanyRef,
): string {
    if (total !== undefined && total > 0)
        return "The jobs on this page are no longer in the catalogue.";
    if (company)
        return status === "queue"
            ? "Nothing from this company in your queue. Queue one of its jobs to have it tailored."
            : "Nothing sent to this company yet. Its jobs you mark applied show here.";
    return status === "queue"
        ? "Nothing in your queue. Queue a job from All jobs to have it tailored."
        : "Nothing sent yet. Jobs you mark applied show here.";
}

/** The count one company's header shows, and the words after it. */
export interface CompanyCount {
    total: number | undefined;
    /** What an envoy tab lists. Null for the header's own words of open jobs. */
    line: string | null;
}

/**
 * The count in one company's header: on In queue and Applied, envoy's
 * total of the person's applications there, with what the tab lists; on
 * every other tab, the company's open jobs in the catalogue.
 */
export function companyCount(
    source: JobsSource,
    status: JobsStatus,
    catalogueTotal: number | undefined,
    envoyTotal: number | undefined,
    company: CompanyRef,
): CompanyCount {
    return source === "envoy" && isTracked(status)
        ? { total: envoyTotal, line: trackedLine(envoyTotal, status, company) }
        : { total: catalogueTotal, line: null };
}

/**
 * The words beside the toolbar's count: what an envoy tab lists, or what
 * the catalogue found for the words searched.
 */
export function boardLine(
    source: JobsSource,
    status: JobsStatus,
    total: number | undefined,
    words: string,
    company?: CompanyRef,
): string {
    return source === "envoy" && isTracked(status)
        ? trackedLine(total, status, company)
        : jobsLine(total, words);
}

/**
 * The results the board shows. An envoy tab's page is already only that
 * tab's; the catalogue's page is held to the tab picked.
 */
export function shownOnBoard<Shown extends SearchPosting>(
    source: JobsSource,
    postings: readonly Shown[],
    status: JobsStatus,
    applications: Readonly<Record<string, Application>> | undefined,
): Shown[] {
    return source === "envoy" ? [...postings] : shownPostings(postings, status, applications);
}

/** The line in place of the results when none shows, for the list the tab reads. */
export function boardEmptyLine(
    source: JobsSource,
    status: JobsStatus,
    found: number,
    total: number | undefined,
    company?: CompanyRef,
): string {
    return source === "envoy" && isTracked(status)
        ? trackedEmptyLine(status, total, company)
        : emptyLine(found, status);
}
